import { useState, useEffect, useRef, useCallback } from 'react';
import { buildWebSocketUrl } from '../utils/websocket';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export function useLiveMatch() {
  const [matchState, setMatchState] = useState(null);
  const [streamInfo, setStreamInfo] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  const connect = useCallback(() => {
    const wsUrl = buildWebSocketUrl('/api/live/ws');
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setError(null);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, WS_PING_INTERVAL);
      };
      
      wsRef.current.onmessage = (event) => {
        if (event.data === 'pong') return;
        
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onerror = () => {
        setError('Connection error');
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Schedule reconnect
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, WS_RECONNECT_DELAY);
      };
      
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      setError('Failed to connect');
    }
  }, []);
  
  const handleMessage = (message) => {
    const { type, data } = message;
    
    switch (type) {
      case 'match_update':
      case 'match_start':
        setMatchState(data);
        // Update stream info from match data if present
        if (data.stream) {
          setStreamInfo(data.stream);
        }
        break;
      
      case 'connected':
        // Connected but no active match - check for stream info
        setMatchState(null);
        if (data.stream) {
          setStreamInfo(data.stream);
        }
        break;
      
      case 'stream_update':
        // Stream status changed
        setStreamInfo(data);
        // Also update match state if present
        setMatchState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            stream: data
          };
        });
        break;
        
      case 'round_end':
        setMatchState(prev => {
          if (!prev) return data;
          return {
            ...prev,
            team1: data.team1,
            team2: data.team2,
            win_probability: data.win_probability ?? prev.win_probability,
            win_prob_history: data.win_prob_history || prev.win_prob_history,
            round_history: [...(prev.round_history || []), {
              round: data.round,
              winner: data.winner,
              winner_side: data.winner_side,
              reason: data.reason,
              team1_score: data.team1_score,
              team2_score: data.team2_score
            }]
          };
        });
        break;
        
      case 'round_start':
        setMatchState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            round_number: data.round_number,
            phase: 'live'
          };
        });
        break;
        
      case 'kill':
        setMatchState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            team1: data.team1,
            team2: data.team2,
            kill_feed: [
              data.kill,
              ...(prev.kill_feed || []).slice(0, 9)
            ]
          };
        });
        break;
        
      case 'match_end':
        setMatchState(prev => ({
          ...prev,
          ...data,
          phase: 'finished',
          is_live: false,
          stream: null  // Clear stream on match end
        }));
        break;
        
      case 'no_match':
        setMatchState(null);
        // Still check for stream info
        if (data.stream) {
          setStreamInfo(data.stream);
        }
        break;
        
      default:
        break;
    }
  };
  
  // Request stream status
  const requestStreamStatus = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('get_stream');
    }
  }, []);
  
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
  }, []);
  
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);
  
  return {
    matchState,
    streamInfo,
    isConnected,
    error,
    reconnect: connect,
    requestStreamStatus
  };
}

export default useLiveMatch;
